import PermissionButton, { currentCan } from '../../permissions/PermissionButton';
import { blockedSalesMessage } from '../shared/SalesBlockedWarning';
import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import {
  Modal,
  Card,
  message,
  notification,
  Button,
  Typography,
  Row,
  Col,
  Spin,
  Alert,
  Tag,
  Table,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Space,
  Select,
  Descriptions,
  Drawer,
  Checkbox,
  Tooltip
} from "antd";

import {
  EditOutlined,
  CheckOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  EyeOutlined,
  CloseOutlined,
  UndoOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  StopOutlined
} from "@ant-design/icons";
import { 
  useFetchDevisById,
  useFetchItemsInDevis,
  useValidateDevis,
  useUnvalidateDevis,
  useUpdateItemInDevis,
  useDeleteItemFromDevis,
  useTransferDevis,
  useUpdateDevis,
  useAddItemToDevis,
  useSaveDevis,
  useDuplicateDevis
} from "../../hooks/devisHooks";
import DevisUpdateForm from "./DevisupdateForm";
import DevisDeleteButton from "./DevisDeleteButton";
import DevisAddItemForm from "./AddItemToDevisForm";
import { usePaymentModes, useDeliveryModes } from '../../hooks/listsHooks';
import ComList from '../com/ComList';
import ClientList from '../client/ClientList';
import { useFetchComById } from "../../hooks/comHooks";
import { getClientByCodeClt } from "../../models/clientAPI"; 
import NestedDevisTable from './NestedDevisTable';
import { printDevis, initializePrintTemplates, previewDevis, printDevisEmplacement, previewDevisEmplacement } from './DevisPrintUtility';

import PrintDialog from "./PrintDialog";
import dayjs from 'dayjs';
import EnhancedEditableField from '../../utils/EnhancedEditableField';
import ChangesSummaryModal from '../../utils/ChangesSummaryModal';
import { motion } from 'framer-motion'; // Assuming you have framer-motion installed
// Removed per-article API fetching in analysis; relying on nested article fields from items
import { getClientById, getAllClients } from '../../models/clientAPI';
import { useFetchAllEntrepots } from '../../hooks/entrepotHooks';

const { Title, Text } = Typography;
const { Option } = Select;

const titleStyle = {
  marginBottom: "4px",
  marginTop: "0px",
};

const formatDate = (date) =>
  date ? dayjs(date).format('DD/MM/YYYY') : '';

const EditableContext = React.createContext(null);

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);

  useEffect(() => {
    if (editing) {
      inputRef.current.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();

      // If PV_TTC is changed, recalculate PV_HT and set REMISE to 0
      if (dataIndex === 'PV_TTC') {
        const newPvTtc = Number(values.PV_TTC);
        const tva = Number(record.TVA) || 0;
        const newPvHt = newPvTtc / (1 + tva / 100);

        handleSave({
          ...record,
          ...values,
          REMISE: 0,
          PV_HT: Math.round(newPvHt * 100) / 100
        });
      } else {
        handleSave({ ...record, ...values });
      }
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[
          {
            required: true,
            message: `${title} est requis.`,
          },
        ]}
      >
        <InputNumber
          ref={inputRef}
          onPressEnter={save}
          onBlur={save}
          min={0}
          step={dataIndex === 'REMISE' ? 1 : 0.01}
          precision={2}
          max={dataIndex === 'REMISE' ? 100 : undefined}
        />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{ paddingRight: 24 }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

const EditableField = ({ 
  value, 
  onChange, 
  options, 
  inputType = "input",
  userPermissions,
  devisStatus,
  displayValue
}) => {
  const canEdit = currentCan('quotes', 'update') && (userPermissions?.isManager || userPermissions?.isDevisManager || (!devisStatus?.VALIDER && !devisStatus?.TRANSFERER));

  const handleChange = (e) => {
    if (!canEdit) return;
    // For Select, e is the value directly, not an event
    const newValue = inputType === "select" ? e : e.target.value;
    onChange(newValue);
  };

  if (inputType === "select") {
    if (!canEdit) {
      // Ensure we render a string, not an object
      const displayText = displayValue || (typeof value === 'string' ? value : (value?.value || value?.label || String(value) || 'N/A'));
      return <span>{displayText}</span>;
    }
    
    // Handle both string and object options
    const getSelectValue = () => {
      if (!value) return undefined;
      if (typeof value === 'string') return value;
      // If value is an object, extract the value property
      return value?.value || value;
    };
    
    return (
      <Select
        value={getSelectValue()}
        onChange={(value) => {
          // Call handleChange which will call the parent onChange
          handleChange(value);
        }}
        style={{ width: '200px' }}
        disabled={!canEdit}
        dropdownMatchSelectWidth={false}
        // Render dropdown into body to avoid being hidden by surrounding cards/overflow
        getPopupContainer={() => document.body}
        // Ensure high stacking order in case of local z-index contexts
        dropdownStyle={{ zIndex: 2000 }}
        allowClear={false}
      >
        {options?.map(option => {
          // Support both string options and object options {value, label}
          const optionValue = typeof option === 'object' ? option.value : option;
          const optionLabel = typeof option === 'object' ? option.label : option;
          return (
            <Option key={optionValue} value={optionValue}>
              {optionLabel}
            </Option>
          );
        })}
      </Select>
    );
  }

  return (
    <Input
      value={typeof value === 'object' ? value?.value || value?.label || '' : value}
      onChange={handleChange}
      style={{ width: '200px' }}
      disabled={!canEdit}
    />
  );
};

const SingleDevis = ({ 
  devisId, 
  onChangeView, 
  userPermissions = {isManager: false, isDevisManager: false},
  onChangesSaved,
  onChangesDetected
}) => {
  const isCompteCD = (compte) => String(compte || '').toUpperCase().startsWith('CD');
  const {
    devis: initialDevis,
    loading: loadingDevis,
    error: errorDevis,
    refetch,
  } = useFetchDevisById(devisId);
  const {
    items: initialItems,
    loading: loadingItems,
    error: errorItems,
    refetch: refetchItems,
  } = useFetchItemsInDevis(devisId);
  const { validate, error: validateError, isValidated } = useValidateDevis();
  const { unvalidate, error: unvalidateError, isUnvalidated } = useUnvalidateDevis();
  const { transfer, error: transferError, isTransferred } = useTransferDevis();
  const { handleSaveDevis, error: saveError, isSaved } = useSaveDevis();
  const { duplicate, error: duplicateError, isDuplicated, newDevis } = useDuplicateDevis();

  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [isAddItemDrawerVisible, setIsAddItemDrawerVisible] = useState(false);

  const [localDevis, setLocalDevis] = useState(null);
  const [localItems, setLocalItems] = useState(null);
  const salesBlockMessage = blockedSalesMessage(localItems);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  const [totals, setTotals] = useState({ totalTTC: 0, totalHT: 0 });
  const [comDrawerOpen, setComDrawerOpen] = useState(false);
  const [selectedCom, setSelectedCom] = useState('');
  const [commercialId, setCommercialId] = useState(null);
  const { Com: commercialData, loading: loadingCom } = useFetchComById(commercialId);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [isSummaryModalVisible, setIsSummaryModalVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isPriceAnalysisOpen, setIsPriceAnalysisOpen] = useState(false);
  const [priceAnalysisRows, setPriceAnalysisRows] = useState([]);
  const [priceAnalysisLoading, setPriceAnalysisLoading] = useState(false);
  const [clientDrawerOpen, setClientDrawerOpen] = useState(false);
  const [refreshingClient, setRefreshingClient] = useState(false);
  const { entrepots, loading: loadingEntrepots } = useFetchAllEntrepots();
  const { modes: paymentModes } = usePaymentModes();
  const { modes: deliveryModes } = useDeliveryModes();
  const [isNotesModalVisible, setIsNotesModalVisible] = useState(false);
  const [isClientNameEditing, setIsClientNameEditing] = useState(false);
  const [clientNameDraft, setClientNameDraft] = useState('');

  // Ensure draft follows loaded devis unless user is currently editing
  useEffect(() => {
    if (!isClientNameEditing) {
      setClientNameDraft(localDevis?.CLIENT || '');
    }
  }, [localDevis?.CLIENT, isClientNameEditing]);

  const runPriceAnalysis = async () => {
    if (!localItems || localItems.length === 0) {
      message.info('Aucun article à analyser.');
      return;
    }
    try {
      setPriceAnalysisLoading(true);
      // Use nested article fields from localItems.item.article
      const rows = localItems.map((it) => {
        const base = it.article || {};
        const basePrice = base?.PV_TTC != null ? Number(base.PV_TTC) : null;
        const devisPrice = Number(it.PV_TTC);
        // Treat null article remise as 0%
        const articleRemise = base?.Remise != null ? Number(base.Remise) : 0;
        const detailRemise = Number(it.REMISE ?? 0);

        const samePrice = basePrice != null && Number(basePrice.toFixed(2)) === Number(devisPrice.toFixed(2));

        const devisTotal = calculateTotalTTC(it.QTE, devisPrice, detailRemise);
        const articleTotal = basePrice != null
          ? calculateTotalTTC(
              it.QTE,
              basePrice,
              articleRemise
            )
          : null;

        const diffArticle = (articleTotal == null) ? null : Number((devisTotal - articleTotal).toFixed(2));

        // Compute total diff percentage based on totals TTC
        const totalsDiffer = (articleTotal != null && Math.abs(devisTotal - articleTotal) >= 0.01);
        const totalDiffPct = (articleTotal == null || articleTotal === 0)
          ? null
          : Number((((devisTotal - articleTotal) / articleTotal) * 100).toFixed(2));

        // Determine a max quantity threshold (prefer STK_MAX, fallback to qte_stk)
        const stkMax = base?.STK_MAX != null ? Number(base.STK_MAX) : null;
        const stock = base?.stocks?.[0];
        const qteStk = stock?.qte_stk != null ? Number(stock.qte_stk) : null;
        // If STK_MAX is exactly 0, do not perform stock check (no fallback)
        const maxQty = (stkMax === 0)
          ? null
          : ((stkMax != null && stkMax > 0) ? stkMax : (qteStk != null ? qteStk : null));
        const exceedsMaxQty = (maxQty != null && Number(it.QTE) > maxQty);

        // Compare remise values (treating null as 0)
        const remiseDiffers = Math.abs(Number(articleRemise.toFixed(2)) - Number(detailRemise.toFixed(2))) >= 0.01;

        // Flag mismatch when quantity exceeds max, totals differ, or remise differs
        return {
          key: `${it.CODE_ART}`,
          CODE_ART: it.CODE_ART,
          ARTICLE: it.ARTICLE || base?.ARTICLE || '',
          QTE: it.QTE,
          REMISE: detailRemise,
          articleRemise,
          TVA: it.TVA,
          devisPV: devisPrice,
          basePV: basePrice,
          devisTotal,
          articleTotal,
          diffArticle,
          totalDiffPct,
          maxQty,
          exceedsMaxQty,
          stkMax,
          qteStk,
          remiseDiffers,
          // Include row if quantity exceeds max, totals differ, or remise differs
          mismatch: exceedsMaxQty || totalsDiffer || remiseDiffers
        };
      }).filter(r => r.mismatch);

      setPriceAnalysisRows(rows);
      setIsPriceAnalysisOpen(true);
    } catch (e) {
      message.error('Erreur lors de l\'analyse des prix');
    } finally {
      setPriceAnalysisLoading(false);
    }
  };

  useEffect(() => {
    if (localDevis?.CODE_COM) {
      setCommercialId(localDevis.CODE_COM);
    }
  }, [localDevis?.CODE_COM]);
  
  useEffect(() => {
    if (commercialData) {
      setSelectedCom(`${commercialData.nom} (${commercialData.code_com})`);
    }
  }, [commercialData]);

  const handleComSelect = (com) => {
    handleFieldChange("CODE_COM", com);
    setCommercialId(com);
    setComDrawerOpen(false);
  };

  const handleClientSelect = (client) => {
    setLocalDevis((prev) => ({
      ...prev,
      CODE_CLT: client.CODE_CLT,
      CLIENT: client.CLIENT,
      COMPTE: client.COMPTE,
      MODE_PAIE: client.MODE_PAIE ?? prev.MODE_PAIE,
      MODELIV: client.MODELIV ?? prev.MODELIV,
      CODE_COM: client.CODE_COM ?? prev.CODE_COM,
      client: {
        ...(prev?.client || {}),
        ...client,
      },
    }));
    if (client?.CODE_COM) {
      setCommercialId(client.CODE_COM);
      setSelectedCom("");
    }
    setHasLocalChanges(true);
    setClientDrawerOpen(false);
    message.success(`Client changé: ${client.CLIENT} (${client.COMPTE})`);
  };

  const handleRefreshClient = async () => {
    if (!localDevis?.CODE_CLT) {
      message.warning('Aucun client associé à ce devis');
      return;
    }

    try {
      setRefreshingClient(true);
      const response = await getClientByCodeClt(localDevis.CODE_CLT);
      const clientData = response.data;

      if (clientData) {
        setLocalDevis((prev) => {
          const cd = isCompteCD(prev?.COMPTE ?? clientData?.COMPTE);
          return {
            ...prev,
            // For compte "CD*", allow per-devis CLIENT override: do not overwrite it on refresh
            CLIENT: cd ? prev.CLIENT : (clientData.CLIENT ?? prev.CLIENT),
            COMPTE: clientData.COMPTE ?? prev.COMPTE,
            client: {
              ...(prev?.client || {}),
              ...clientData,
              conditions: clientData.conditions || prev?.client?.conditions,
            },
          };
        });
        setHasLocalChanges(true);
        message.success('Données client actualisées avec succès');
      } else {
        message.error('Client non trouvé');
      }
    } catch (error) {
      console.error('Erreur lors de l\'actualisation du client:', error);
      message.error(`Échec de l'actualisation: ${error.message || 'Erreur inconnue'}`);
    } finally {
      setRefreshingClient(false);
    }
  };
  
  const refreshClientInfo = async () => {
    try {
      if (!localDevis) return;
      setRefreshingClient(true);
      // Prefer id from joined client; fallback by COMPTE search
      let latestClient = null;
      if (localDevis.client?.id) {
        const res = await getClientById(localDevis.client.id);
        latestClient = res.data;
      } else if (localDevis.COMPTE) {
        const result = await getAllClients({ compteSearch: localDevis.COMPTE, pageSize: 1 });
        latestClient = result.clients?.[0] || null;
      }
      if (!latestClient) {
        message.warning("Client introuvable pour actualisation.");
        return;
      }
      setLocalDevis((prev) => {
        const cd = isCompteCD(prev?.COMPTE ?? latestClient?.COMPTE);
        return {
          ...prev,
          CODE_CLT: latestClient.CODE_CLT ?? prev.CODE_CLT,
          // For compte "CD*", allow per-devis CLIENT override: do not overwrite it on refresh
          CLIENT: cd ? prev.CLIENT : (latestClient.CLIENT ?? prev.CLIENT),
          COMPTE: latestClient.COMPTE ?? prev.COMPTE,
          MODELIV: latestClient.MODELIV ?? prev.MODELIV,
          MODE_PAIE: latestClient.MODE_PAIE ?? prev.MODE_PAIE,
          client: {
            ...(prev?.client || {}),
            ...latestClient,
          },
        };
      });
      setHasLocalChanges(true);
      message.success("Infos client actualisées.");
    } catch (e) {
      message.error(`Échec d'actualisation du client: ${e.message || e}`);
    } finally {
      setRefreshingClient(false);
    }
  };
  
  const prepareDevisForSave = (devis) => {
    const { comercial, entrepot, client, ...cleanDevis } = devis;
    console.log("clean:", cleanDevis);
    return {
      ...cleanDevis,
      CODE_COM: parseInt(cleanDevis.CODE_COM),
      CODE_CLT: cleanDevis.CODE_CLT,
      MNT_HT: parseFloat(cleanDevis.MNT_HT || 0),
      MNT_TTC: parseFloat(cleanDevis.MNT_TTC || 0),
      REMISEG: cleanDevis.REMISEG ? parseFloat(cleanDevis.REMISEG) : null,
      CODE_ENT: cleanDevis.CODE_ENT || null,
    };
  };

  // Helper function to calculate TTC for a single item
  const calculateTotalTTC = (qte, pvTTC, remise) => {
    const discountedPrice = (pvTTC * (1 - remise / 100));
    const total = qte * discountedPrice;
    return Math.round(total * 100) / 100;
  };

  // Calculate totals for all items
  const calculateTotals = (items) => {
    let totalTTC = 0;
    let totalHT = 0;

    const updatedItems = items.map((item) => {
      // Calculate TTC independently from PV_TTC
      const itemTotalTTC = calculateTotalTTC(item.QTE, item.PV_TTC, item.REMISE);

      // Calculate HT independently from PV_HT (not derived from TTC)
      const discountedPriceHT = item.PV_HT * (1 - item.REMISE / 100);
      const itemTotalHT = Math.round((item.QTE * discountedPriceHT) * 100) / 100;

      totalTTC += itemTotalTTC;
      totalHT += itemTotalHT;

      return {
        ...item,
        TotalHT: itemTotalHT,
        TotalTTC: itemTotalTTC
      };
    });

    // Round final totals to 2 decimal places
    totalTTC = Math.round(totalTTC * 100) / 100;
    totalHT = Math.round(totalHT * 100) / 100;

    return {
      updatedItems,
      totalTTC,
      totalHT
    };
  };

  // Calculate totals using useMemo to avoid recalculation on every render
  const calculatedTotals = useMemo(() => {
    if (!localItems || localItems.length === 0) return null;
    return calculateTotals(localItems);
  }, [localItems]);

  // Add a manual trigger for recalculation (used by the table)
  const triggerRecalculation = React.useCallback(() => {
    console.log("Manual recalculation triggered");
    // Force update of local items to trigger useMemo recalculation
    setLocalItems(prevItems => [...prevItems]);
  }, []);

  // Update devis and items when calculated totals change
  useEffect(() => {
    if (!calculatedTotals) return;

    const { totalTTC, totalHT, updatedItems } = calculatedTotals;

    // Update totals state
    setTotals({ totalTTC, totalHT });

    // Update devis totals
    setLocalDevis(prevDevis => ({
      ...prevDevis,
      MNT_TTC: totalTTC,
      MNT_HT: totalHT
    }));

    // Only update items if TotalHT/TotalTTC have actually changed
    const hasItemsChanged = !localItems.every((item, index) => {
      const updatedItem = updatedItems[index];
      return updatedItem &&
             item.TotalHT === updatedItem.TotalHT &&
             item.TotalTTC === updatedItem.TotalTTC;
    });

    if (hasItemsChanged) {
      setLocalItems(updatedItems);
    }
  }, [calculatedTotals]);

  // Function to detect keywords in a text field
  const detectKeywords = (text) => {
    if (!text) return { AJD: false, NOS_SOINS: false, SUR_PLACE: false };
    
    const upperText = text.toUpperCase();
    return {
      AJD: upperText.includes('AJD'),
      NOS_SOINS: upperText.includes('NOS SOINS') || upperText.includes('NOS_NOINS'),
      SUR_PLACE: upperText.includes('SUR PLACE') || upperText.includes('SURPLACE')
    };
  };

  // Function to update checkboxes from all sources with priority
  // Priority: REMARQUE (highest) > client.conditions > MODELIV (devis) > MODELIV (client)
  const updateCheckboxesFromAllSources = (devis) => {
    if (!devis) return { AJD: false, NOS_SOINS: false, SUR_PLACE: false };
    
    const remarqueKeywords = detectKeywords(devis.REMARQUE);
    const clientConditionsKeywords = detectKeywords(devis.client?.conditions);
    const devisModelivKeywords = detectKeywords(devis.MODELIV);
    const clientModelivKeywords = detectKeywords(devis.client?.MODELIV);
    
    // REMARQUE has highest priority - if found in REMARQUE, it always wins
    // Otherwise check client conditions, then MODELIV sources
    return {
      AJD: remarqueKeywords.AJD || clientConditionsKeywords.AJD || devisModelivKeywords.AJD || clientModelivKeywords.AJD,
      NOS_SOINS: remarqueKeywords.NOS_SOINS || clientConditionsKeywords.NOS_SOINS || devisModelivKeywords.NOS_SOINS || clientModelivKeywords.NOS_SOINS,
      SUR_PLACE: remarqueKeywords.SUR_PLACE || clientConditionsKeywords.SUR_PLACE || devisModelivKeywords.SUR_PLACE || clientModelivKeywords.SUR_PLACE
    };
  };

  // Check if REMARQUE contains keywords (for protection logic)
  const remarqueHasKeywords = (remarque) => {
    if (!remarque) return { AJD: false, NOS_SOINS: false, SUR_PLACE: false };
    return detectKeywords(remarque);
  };

  // Effect to update checkboxes when REMARQUE, client conditions, or MODELIV changes
  useEffect(() => {
    if (localDevis) {
      const checkboxes = updateCheckboxesFromAllSources(localDevis);
      const remarqueKeywords = remarqueHasKeywords(localDevis.REMARQUE);
      
      setLocalDevis(prevDevis => {
        if (!prevDevis) return prevDevis;
        
        const updates = {};
        let hasChanges = false;
        
        // AJD is ONLY driven by REMARQUE - auto check/uncheck based on presence in REMARQUE
        // If "AJD" is in REMARQUE, check it; if not, uncheck it
        if (remarqueKeywords.AJD && !prevDevis.AJD) {
          updates.AJD = true;
          hasChanges = true;
        } else if (!remarqueKeywords.AJD && prevDevis.AJD) {
          updates.AJD = false;
          hasChanges = true;
        }
        
        // NOS_SOINS and SUR_PLACE keep the original logic (client conditions, MODELIV)
        if (remarqueKeywords.NOS_SOINS) {
          // REMARQUE has it, always check
          if (!prevDevis.NOS_SOINS) {
            updates.NOS_SOINS = true;
            hasChanges = true;
          }
        } else if (checkboxes.NOS_SOINS && !prevDevis.NOS_SOINS) {
          updates.NOS_SOINS = true;
          hasChanges = true;
        }
        
        if (remarqueKeywords.SUR_PLACE) {
          // REMARQUE has it, always check
          if (!prevDevis.SUR_PLACE) {
            updates.SUR_PLACE = true;
            hasChanges = true;
          }
        } else if (checkboxes.SUR_PLACE && !prevDevis.SUR_PLACE) {
          updates.SUR_PLACE = true;
          hasChanges = true;
        }
        
        if (hasChanges) {
          setHasLocalChanges(true);
          return { ...prevDevis, ...updates };
        }
        return prevDevis;
      });
    }
  }, [localDevis?.REMARQUE, localDevis?.MODELIV, localDevis?.client?.conditions, localDevis?.client?.MODELIV]);

  const handleFieldChange = (field, value) => {
    setLocalDevis(prevDevis => ({
      ...prevDevis,
      [field]: value
    }));
    console.log(localDevis);
    setHasLocalChanges(true);
  };

  // Handle checkbox change with protection against unchecking if text is present in REMARQUE
  const handleCheckboxChange = (field, checked) => {
    const remarque = localDevis?.REMARQUE || '';
    const upperRemarque = remarque.toUpperCase();
    
    // AJD is completely controlled by REMARQUE - manual changes are not allowed
    if (field === 'AJD') {
      message.info('AJD est contrôlé automatiquement par le contenu de la remarque.');
      return;
    }
    
    // For NOS_SOINS and SUR_PLACE: prevent unchecking if the keyword is present in REMARQUE
    if (!checked) {
      if (field === 'NOS_SOINS' && (upperRemarque.includes('NOS SOINS') || upperRemarque.includes('NOS_NOINS'))) {
        message.warning('Impossible de décocher NOS_SOINS car le texte correspondant est présent dans la remarque.');
        return;
      }
      if (field === 'SUR_PLACE' && (upperRemarque.includes('SUR PLACE') || upperRemarque.includes('SURPLACE'))) {
        message.warning('Impossible de décocher SUR_PLACE car le texte correspondant est présent dans la remarque.');
        return;
      }
    }
    
    handleFieldChange(field, checked);
  };

  // Initialize or update devis data when the server devis changes
  useEffect(() => {
    if (initialDevis) {
      // Update checkboxes based on all sources (REMARQUE, client conditions, MODELIV) when initializing
      const checkboxes = updateCheckboxesFromAllSources(initialDevis);
      const remarqueKeywords = remarqueHasKeywords(initialDevis.REMARQUE);
      
      setLocalDevis({ 
        ...initialDevis,
        // AJD is ONLY controlled by REMARQUE - check only if "AJD" is in REMARQUE
        AJD: remarqueKeywords.AJD,
        // NOS_SOINS and SUR_PLACE use the original logic (REMARQUE, client conditions, MODELIV)
        NOS_SOINS: remarqueKeywords.NOS_SOINS || checkboxes.NOS_SOINS || initialDevis.NOS_SOINS || false,
        SUR_PLACE: remarqueKeywords.SUR_PLACE || checkboxes.SUR_PLACE || initialDevis.SUR_PLACE || false
      });
    }
  }, [initialDevis]);

  // Initialize or update items when the server items change
  useEffect(() => {
    if (initialItems) {
      setLocalItems([...initialItems]);
    }
  }, [initialItems]);

  useEffect(() => {
    console.log("localItems/Devis have been updated:", localItems, localDevis);
  }, [localItems, localDevis]);
  
  const handleSave = (row) => {
    setLocalItems((prevItems) =>
      prevItems.map((item) =>
        item.CODE_ART === row.CODE_ART ? { ...item, ...row } : item
      )
    );
    setHasLocalChanges(true);
    
  };
  
  const handleAddItemSuccess = (payload) => {
    const itemsToAdd = Array.isArray(payload) ? payload : [payload];
    setLocalItems((prevItems) => {
      const updated = [...prevItems];
      itemsToAdd.forEach((newItem) => {
        if (!newItem || !newItem.CODE_ART) return;
        const existingIndex = updated.findIndex((it) => it.CODE_ART === newItem.CODE_ART);
        if (existingIndex !== -1) {
          updated[existingIndex] = { ...updated[existingIndex], ...newItem };
        } else {
          updated.push(newItem);
        }
      });
      return updated;
    });
    setHasLocalChanges(true);
    message.success(itemsToAdd.length > 1 ? `${itemsToAdd.length} articles ajoutés` : 'Article ajouté');
  };
  
  const handleDelete = (codeArt) => {
    setLocalItems((prevItems) => prevItems.filter(item => item.CODE_ART !== codeArt));
    setHasLocalChanges(true);
    message.success("Article supprimé localement. Cliquez sur 'Enregistrer' pour sauvegarder."); 
  };
  
  const handleUpdateDevis = (updatedData) => {
    setLocalDevis((prevDevis) => ({ ...prevDevis, ...updatedData }));
    setHasLocalChanges(true);
    setIsUpdateModalVisible(false);
  };

  const handleSaveChanges = () => {
    setIsSummaryModalVisible(true);
  };
  
  useEffect(() => {
    if (hasLocalChanges && onChangesDetected) {
      onChangesDetected();
    }
  }, [hasLocalChanges, onChangesDetected]);

  const confirmAndSaveChanges = async () => {
    try {
      setIsSaving(true);
      const dataToSave = await new Promise((resolve) => {
        setLocalDevis((currentDevis) => {
          setLocalItems((currentItems) => {
            const cleanItems = currentItems.map(({ article, ...item }) => item);
            
            const data = {
              devis: prepareDevisForSave(currentDevis),
              items: cleanItems
            };
            resolve(data);
            return currentItems;
          });
          return currentDevis;
        });
      });
  
      console.log("Data to Save:", dataToSave);
      await handleSaveDevis(dataToSave);
      setHasLocalChanges(false);
      if (onChangesSaved) {
        onChangesSaved();
      }
      setIsSummaryModalVisible(false);
      setIsSaving(false);
      message.success("Devis enregistré avec succès!");
      refetch();
      refetchItems();
    } catch (error) {
      console.error("Error saving devis:", error);
      message.error(`Échec de l'enregistrement du devis: ${error.message || error}`);
      setIsSaving(false);
    }
  };
  
  const handleValidateClick = async () => {
    if (salesBlockMessage) return message.error(salesBlockMessage);
    try {
      // Check if client is blocked
      if (localDevis.client?.BLOQUER === true) {
        message.error('Impossible de valider le devis. Le client est bloqué.');
        return;
      }

      // Check if there are items with quantity 0 or less
      const itemsWithZeroQty = localItems?.filter(item => !item.QTE || item.QTE <= 0) || [];
      
      if (itemsWithZeroQty.length > 0) {
        const itemCodes = itemsWithZeroQty.map(item => item.CODE_ART).join(', ');
        message.error(`Impossible de valider le devis. Les articles suivants ont une quantité nulle ou invalide: ${itemCodes}`);
        return;
      }

      // Check if there are no items at all
      if (!localItems || localItems.length === 0) {
        message.error('Impossible de valider le devis. Aucun article n\'a été ajouté.');
        return;
      }

      const validatedDevis = await validate(devisId);
      setLocalDevis(current => ({ ...current, ...validatedDevis }));
      message.success('Le devis a été validé avec succès.');
      refetch();
    } catch (error) {
      message.error(`Échec de la validation du devis: ${error}`); 
    }
  };

  const handleUnvalidateClick = async () => {
    try {
      await unvalidate(devisId);
      message.success('Le devis a été dévalidé avec succès.');
      refetch();
    } catch (error) {
      message.error(`Échec de la dévalidation du devis: ${error}`);
    }
  };

  const handleTransferClick = async () => {
    try {
      await transfer(devisId);
      notification.success({message:'Le devis a été transféré avec succès.',
        description: 'Un bon de livraison a été créé',
      placement: 'bottomLeft',
      }); 

      refetch();
      handleClose();
    } catch (error) {
      message.error(`Échec du transfert du devis: ${error}`);
    }
  };

  const handleDuplicateClick = async () => {
    try {
      const dup = await duplicate(localDevis.id);
      message.success(`Devis dupliqué: ${dup.REF_DEV}`);
      if (onChangeView) {
        onChangeView('list');
      } else {
        refetch();
      }
    } catch (e) {
      message.error(`Échec de la duplication: ${e.message || e}`);
    }
  };



  const handlePrintClick = () => {
    setIsPrintDialogOpen(true);
  };

  useEffect(() => {
    initializePrintTemplates().catch(console.error);
  }, []);


  const handlePrintEmplacement = () => {
    // Call the specific emplacement print function
    printDevisEmplacement(localDevis, commercialData);
  };
  
  const handlePreviewEmplacement = () => {
    // Call the specific emplacement preview function
    previewDevisEmplacement(localDevis, commercialData);
  };

  const handlePrint = async (printType) => {
    try {
      if (!localDevis || !localItems) {
        throw new Error('Missing required data for printing');
      }

      await printDevis(localDevis, localItems, commercialData, printType);
      message.success('Document préparé pour l\'impression');
    } catch (error) {
      console.error('Error printing devis:', error);
      message.error(`Échec de l'impression: ${error.message}`);
    }
  };

  const handlePreview = async (printType) => {
    try {
      if (!localDevis || !localItems) {
        throw new Error('Missing required data for preview');
      }
      
      // Call the enhanced previewDevis function
      await previewDevis(localDevis, localItems, commercialData, printType);
    } catch (error) {
      console.error('Error previewing devis:', error);
      message.error(`Échec de l'aperçu: ${error.message}`);
    }
  };
  const handleClose = () => {

            onChangeView('list');

  };

  const columns = [
    {
      title: "Code Art",
      dataIndex: "CODE_ART",
      key: "CODE_ART",
    },
    {
      title: "Article",
      dataIndex: "ARTICLE",
      key: "ARTICLE",
    },
    {
      title: "Quantity",
      dataIndex: "QTE",
      key: "QTE",
      editable: true,
    },
    {
      title: "PV TTC",
      dataIndex: "PV_TTC",
      key: "PV_TTC",
      editable: true,
      render: (text) => ` ${parseFloat(text).toFixed(2)}`,
    },
    {
      title: "Discount (%)",
      dataIndex: "REMISE",
      key: "REMISE",
      editable: true,
      render: (text) => `${parseFloat(text).toFixed(2)}%`,
    },
    {
      title: "VAT (%)",
      dataIndex: "TVA",
      key: "TVA",
      render: (text) => `${parseFloat(text).toFixed(2)}%`,
    },
    {
      title: "Total TTC",
      dataIndex: "TotalTTC",
      key: "TotalTTC",
      render: (_, record) => ` ${calculateTotalTTC(record.QTE, record.PV_TTC, record.REMISE)}`,
    },
    {
      title: "UAV",
      dataIndex: ["article", "uav"],
      key: "uav",
      render: (text) => text || 'N/A',
    },
    {
      title: "Qty per Carton",
      dataIndex: ["article", "qt_cart"],
      key: "qt_cart",
      render: (text) => text || 'N/A',
    },
    {
      title: "Qty per Package",
      dataIndex: ["article", "q_paquet"],
      key: "q_paquet",
      render: (text) => text || 'N/A',
    },
    {
      title: "REF OEM",
      dataIndex: ["article", "REF_OEM"],
      key: "REF_OEM",
      render: (text) => text || 'N/A',
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Popconfirm
  title="Êtes-vous sûr de vouloir supprimer cet article?"
  onConfirm={() => handleDelete(record.CODE_ART)}
  okText="Oui"
  cancelText="Non"
>
          <PermissionButton permissionModule="quotes" permissionAction="delete" icon={<DeleteOutlined />} danger>
          Supprimer
          </PermissionButton>
        </Popconfirm>
      ),
    },
  ];
  
  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const editableColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

// Replace the getDescriptionItems function with this updated version
// that uses French labels matching the UI screenshot

const getDescriptionItems = () => [
  {
    key: 'clientInfo',
    label: 'Client',
    children: (
      <div>
        <Row gutter={16}>
          <Col span={14}>
            <Text strong>Client:</Text>{' '}
            {!isClientNameEditing ? (
              <Space size="small" wrap>
                <span>
                  {localDevis.CLIENT}
                  {localDevis.client?.VILLE ? ` - ${localDevis.client?.VILLE}` : ''}
                </span>
                {isCompteCD(localDevis?.COMPTE) && (
                  <>
                    <Tooltip title="Compte CD: vous pouvez modifier le nom client uniquement sur ce devis">
                      <Tag color="blue">CD</Tag>
                    </Tooltip>
                    <PermissionButton permissionModule="quotes" permissionAction="update"
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      style={{ padding: '0 4px' }}
                      onClick={() => {
                        setClientNameDraft(localDevis?.CLIENT || '');
                        setIsClientNameEditing(true);
                      }}
                      disabled={!(userPermissions?.isManager || userPermissions?.isDevisManager || (!localDevis?.VALIDER && !localDevis?.TRANSFERER))}
                      title="Modifier le nom client (uniquement sur ce devis)"
                    />
                  </>
                )}
              </Space>
            ) : (
              <Space size="small" wrap>
                <Input
                  value={clientNameDraft}
                  onChange={(e) => setClientNameDraft(e.target.value)}
                  style={{ width: 260 }}
                  placeholder="Nom client (sur ce devis)"
                  disabled={!(userPermissions?.isManager || userPermissions?.isDevisManager || (!localDevis?.VALIDER && !localDevis?.TRANSFERER))}
                />
                <PermissionButton permissionModule="quotes" permissionAction="update"
                  type="primary"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={() => {
                    handleFieldChange("CLIENT", clientNameDraft);
                    setIsClientNameEditing(false);
                    message.success("Nom client modifié localement. Cliquez sur 'Enregistrer Modif.' pour sauvegarder.");
                  }}
                  disabled={!isCompteCD(localDevis?.COMPTE)}
                />
                <PermissionButton permissionModule="quotes" permissionAction="update"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => {
                    setClientNameDraft(localDevis?.CLIENT || '');
                    setIsClientNameEditing(false);
                  }}
                />
                <Tooltip title="Compte CD: modification du nom client uniquement sur ce devis (n'affecte pas la fiche client)">
                  <Tag color="blue">CD</Tag>
                </Tooltip>
              </Space>
            )}
            {localDevis.client?.BLOQUER && (
              <Tooltip title="Ce client est bloqué">
                <StopOutlined 
                  style={{ 
                    color: '#ff4d4f', 
                    fontSize: '16px', 
                    marginLeft: '8px',
                    cursor: 'help'
                  }} 
                />
              </Tooltip>
            )}
          </Col>
          <Col span={8}>
            <Text strong>Compte:</Text> {localDevis.COMPTE || "N/A"}
            <Space size="small" style={{ marginLeft: 8 }}>
              {(userPermissions.isManager || userPermissions.isDevisManager || (!localDevis.VALIDER && !localDevis.TRANSFERER)) && (
                <PermissionButton permissionModule="quotes" permissionAction="update"
                  onClick={() => setClientDrawerOpen(true)} 
                  type="link"
                  icon={<EditOutlined />}
                  size="small"
                  style={{ padding: '0 4px' }}
                >
                  
                </PermissionButton>
              )}
              <Button 
                onClick={refreshClientInfo}
                type="link"
                icon={<ReloadOutlined />}
                size="small"
                loading={refreshingClient}
                style={{ padding: '0 4px' }}
                title="Actualiser les données client"
              />
            </Space>
          </Col>
        </Row>
        <Row style={{ marginTop: '8px' }}>
          <Col span={24}>
            <Text strong>Conditions:</Text> {localDevis.client?.conditions || "Aucune"}
            <Tooltip 
              title={localDevis.client?.NOTES || "pas de notes"}
              placement="top"
            >
              <Button
                type="text"
                icon={<InfoCircleOutlined />}
                onClick={() => setIsNotesModalVisible(true)}
                style={{ 
                  color: '#1890ff', 
                  padding: '0 4px',
                  marginLeft: '8px',
                  height: 'auto'
                }}
                size="small"
              />
            </Tooltip>
          </Col>
        </Row>
      </div>
    ),
    span: 2,
  },
  {
    key: 'entrepot',
    label: 'Entrepôt',
    children: (
      <EditableField
        value={localDevis.CODE_ENT}
        onChange={(value) => {
          handleFieldChange("CODE_ENT", value);
        }}
        options={entrepots?.map(e => ({ value: e.code_ent, label: `${e.code_ent} - ${e.name}` })) || []}
        inputType="select"
        userPermissions={userPermissions}
        devisStatus={{
          VALIDER: localDevis.VALIDER,
          TRANSFERER: localDevis.TRANSFERER
        }}
        displayValue={localDevis.entrepot ? `${localDevis.entrepot.code_ent} - ${localDevis.entrepot.name}` : (localDevis.CODE_ENT || 'Non défini')}
      />
    ),
    span: 1,
  }, 
  
  {
    key: 'amountTTC',
    label: 'Total(ttc)',
    children: ` ${localDevis.MNT_TTC?.toFixed(2)}`,
    span: 1,
  },
  
  {
    key: 'amountHT',
    label: 'Total(ht)',
    children: ` ${localDevis.MNT_HT?.toFixed(2)}`,
    span: 1,
  },
  {
    key: 'creationDate',
    label: 'Date Devis',
    children: formatDate(localDevis.DATE_DEV),
    span: 1,
  },
 
  {
    key: 'paymentMode',
    label: 'M. paiem.',
    children: (
      <EditableField
        value={localDevis.MODE_PAIE}
        onChange={(value) => handleFieldChange("MODE_PAIE", value)}
        options={paymentModes}
        inputType="select"
        userPermissions={userPermissions}
        devisStatus={{
          VALIDER: localDevis.VALIDER,
          TRANSFERER: localDevis.TRANSFERER
        }}
      />
    ),
    span: 1,
  },

  {
    key: 'commercialCode',
    label: 'Commercial',
    children: (
      <Space>
        {loadingCom ? (
          <Spin size="small" />
        ) : (
          <span>
            {selectedCom || (commercialData ? 
              `${commercialData.nom} (${commercialData})` : 
              `ID: ${localDevis.CODE_COM}`)}
          </span>
        )}
        {(userPermissions.isManager || userPermissions.isDevisManager || (!localDevis.VALIDER && !localDevis.TRANSFERER)) && (
          <PermissionButton permissionModule="quotes" permissionAction="update"
            onClick={() => setComDrawerOpen(true)} 
            type="link"
            icon={<EditOutlined />}
            size="small"
            style={{ padding: '4px' }}
          />
        )}
      </Space>
    ),
    span: 1,
  },

  
  {
    key: 'validationDate',
    label: 'Date Validation',
    children: localDevis.DATEVALID ? formatDate(localDevis.DATEVALID) : 'Non validé',
    span: 1,
  },

  {
    key: 'deliveryMode',
    label: 'M. livrais.',
    children: (
      <EditableField
        value={localDevis.MODELIV}
        onChange={(value) => handleFieldChange("MODELIV", value)}
        options={deliveryModes}
        inputType="select"
        userPermissions={userPermissions}
        devisStatus={{
          VALIDER: localDevis.VALIDER,
          TRANSFERER: localDevis.TRANSFERER
        }}
      />
    ),
    span: 1,
  },
  {
    key: 'remarks',
    label: 'Remarque',
    children: (
      <EnhancedEditableField
        value={localDevis.REMARQUE}
        onChange={(value) => handleFieldChange("REMARQUE", value)}
        isLongText={true}
        userPermissions={userPermissions}
        devisStatus={{
          VALIDER: localDevis.VALIDER,
          TRANSFERER: localDevis.TRANSFERER
        }}
      />
    ),
    span: 2,
  },
  {
    key: 'checkboxes',
    label: 'Options',
    children: (
      <Space size="middle">
        <Checkbox
          checked={localDevis.AJD || false}
          onChange={(e) => handleCheckboxChange('AJD', e.target.checked)}
          disabled={true}
          title="AJD est contrôlé automatiquement par le contenu de la remarque"
        >
          AJD
        </Checkbox>
        <Checkbox
          checked={localDevis.NOS_SOINS || false}
          onChange={(e) => handleCheckboxChange('NOS_SOINS', e.target.checked)}
          disabled={!(userPermissions.isManager || userPermissions.isDevisManager || (!localDevis.VALIDER && !localDevis.TRANSFERER))}
        >
          NOS_NOINS
        </Checkbox>
        <Checkbox
          checked={localDevis.SUR_PLACE || false}
          onChange={(e) => handleCheckboxChange('SUR_PLACE', e.target.checked)}
          disabled={!(userPermissions.isManager || userPermissions.isDevisManager || (!localDevis.VALIDER && !localDevis.TRANSFERER))}
        >
          SURPLACE
        </Checkbox>
      </Space>
    ),
    span: 1,
  },
];

  if (loadingDevis || loadingItems) return <Spin tip="Loading..." />;
  if (errorDevis || errorItems)
    return (
      <Alert
        message="Error"
        description={errorDevis || errorItems}
        type="error"
        showIcon
      />
    );

  if (!localDevis || !localItems) return <Alert message="Aucun devis trouvé" type="info" showIcon />; 

  // Animation variants - smooth professional animation
  const containerVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.95,
      transition: { 
        duration: 0.15,
        ease: [0.25, 0.1, 0.25, 1.0]  // EaseOutQuart - smoother animation
      }
    },
    visible: { 
      opacity: 1,
      scale: 1,
      transition: { 
        duration: 0.15,
        ease: [0.25, 0.1, 0.25, 1.0]  // EaseOutQuart
      }
    },
    exit: { 
      opacity: 0,
      scale: 0.97,
      transition: { 
        duration: 0.12,
        ease: [0.25, 0.1, 0.25, 1.0]  // EaseOutQuart
      }
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate={isClosing ? "exit" : "visible"}
      variants={containerVariants}
      style={{
        transformOrigin: 'center center',
        willChange: 'transform, opacity'
      }}
    >
      <Card
        title={
          <>
            Devis - {localDevis.REF_DEV}
            {localDevis.TRANSFERER ? (
              <Tag color="blue" style={{ marginLeft: "8px" }}>
                Transféré
              </Tag>
            ) : localDevis.VALIDER ? (
              <>
  <Tag color="green" style={{ marginLeft: "8px" }}>
    Validé
  </Tag>
</>
            ) : (
              <Tag color="orange" style={{ marginLeft: "8px" }}>
                Enregistré
              </Tag>
            )}

            {localDevis.DATEVALID ? (
    <span style={{ color: "#888888", fontSize: "0.85em", marginLeft: "4px" }}>
      {formatDate(localDevis.DATEVALID) + ' ' + localDevis.HEUREVALID} 
    </span>
  ) : ''}
          </>
        }
        bordered={false}
        extra={
          <Space>
  {hasLocalChanges && (
    <PermissionButton
      icon={<UndoOutlined />}
      onClick={() => {
        Modal.confirm({
          title: 'Annuler les modifications',
          content: 'Êtes-vous sûr de vouloir annuler toutes les modifications? Cela rétablira toutes les modifications à l\'état sauvegardé précédemment.',
          okText: 'Oui, Annuler tout',
          cancelText: 'Non, Garder les modifications',
          onOk: () => {
            setLocalDevis({ ...initialDevis });
            setLocalItems([...initialItems]);
            setHasLocalChanges(false);
            message.success('Toutes les modifications ont été annulées');
          }
        });
      }}
    >
      Annuler modif.
    </PermissionButton>
            )}
            {!localDevis.VALIDER && !localDevis.TRANSFERER && (
              <Tooltip title={salesBlockMessage || (localDevis.client?.BLOQUER ? "Impossible de valider: le client est bloqué" : "")}>
                <PermissionButton permissionModule="quotes" permissionAction="validate"
                  type="primary" 
                  onClick={handleValidateClick} 
                  disabled={
                    hasLocalChanges || Boolean(salesBlockMessage) ||
                    !localItems || 
                    localItems.length === 0 || 
                    localItems.some(item => !item.QTE || item.QTE <= 0) ||
                    localDevis.client?.BLOQUER === true
                  }
                >
                  Valider DV
                </PermissionButton>
              </Tooltip>
            )}
            {(userPermissions.isManager || userPermissions.isDevisManager) && localDevis.VALIDER && !localDevis.TRANSFERER && (
              <PermissionButton permissionModule="quotes" permissionAction="unvalidate" onClick={handleUnvalidateClick}>
                Dévalider
              </PermissionButton>
            )}
            {localDevis.VALIDER === true && !localDevis.TRANSFERER && (
              <PermissionButton permissionModule="quotes" permissionAction="transfer" type="primary" onClick={handleTransferClick} disabled={hasLocalChanges}>
                Transferer {'>'}  Module BL
              </PermissionButton>
            )}
            <PermissionButton permissionModule="quotes" permissionAction="update"
              type="primary"
              onClick={handleSaveChanges}
              disabled={!hasLocalChanges}
            >
              Enregistrer Modif.
            </PermissionButton>
            <PermissionButton permissionModule="quotes" permissionAction="duplicate" onClick={handleDuplicateClick}>
              Dupliquer
            </PermissionButton>
            <PermissionButton permissionModule="quotes" permissionAction="print" onClick={handlePrintClick}>
            Imprimer
            </PermissionButton>
            <Button 
  type="default"
  onClick={handleClose}
  style={{ 
    backgroundColor: 'white',
    color: '#EE2625',
    border: '1px solid #EE2625',  // added red outline
  }}
>
  SORTIR
</Button>

</Space>
        }
        style={{ position: 'relative' }}
      >
        <Descriptions
          bordered
          column={{
            xs: 1,
            sm: 2,
            md: 3,
          }}
          size="small"
          items={getDescriptionItems()}
          labelStyle={{ fontWeight: 'bold', textAlign: 'right' }}
          contentStyle={{ minWidth: '150px' }}
        />
      </Card>

      <Card title="Articles dans le devis" style={{ marginTop: 20 }} extra={
        <Space>
          <PermissionButton permissionModule="quotes" permissionAction="update"
            onClick={() => setIsAddItemDrawerVisible(true)}
            type="primary"
            disabled={!(userPermissions.isManager || userPermissions.isDevisManager) && (localDevis.VALIDER || localDevis.TRANSFERER)}
          >
            Ajouter Article
          </PermissionButton>
          <Button onClick={runPriceAnalysis} loading={priceAnalysisLoading}>
            Analyser Prix
          </Button>
        </Space>
      }>
        <NestedDevisTable
          items={localItems}
          handleSave={handleSave}
          handleDelete={handleDelete}
          calculateTotalTTC={calculateTotalTTC}
          userPermissions={userPermissions}
          devisStatus={{
            VALIDER: localDevis.VALIDER,
            TRANSFERER: localDevis.TRANSFERER
          }}
          codeEnt={localDevis.CODE_ENT}
          onValueChange={triggerRecalculation}
        />
      </Card>

      <Drawer
        title="Analyse des prix (Articles non concordants)"
        width={1000}
        onClose={() => setIsPriceAnalysisOpen(false)}
        open={isPriceAnalysisOpen}
      >
        <Table
          size="small"
          dataSource={priceAnalysisRows}
          pagination={{ pageSize: 10 }}
          columns={[
            { title: 'Code Art', dataIndex: 'CODE_ART', key: 'CODE_ART' },
            { title: 'Article', dataIndex: 'ARTICLE', key: 'ARTICLE' },
            { 
              title: 'Qté', 
              dataIndex: 'QTE', 
              key: 'QTE',
              render: (text, r) => (
                r.exceedsMaxQty 
                  ? <span style={{ color: '#d4380d', fontWeight: 600 }}>{text}</span>
                  : <span>{text}</span>
              )
            },
            {
              title: 'Qté max',
              key: 'maxQty',
              render: (_, r) => r.maxQty == null ? 'N/A' : Number(r.maxQty).toFixed(2)
            },
            {
              title: 'PV TTC',
              key: 'pvttcMerged',
              render: (_, r) => (
                <div>
                  <div><strong>Devis:</strong> {Number(r.devisPV).toFixed(2)}</div>
                  <div><strong>Article:</strong> {r.basePV == null ? 'N/A' : Number(r.basePV).toFixed(2)}</div>
                </div>
              )
            },
            {
              title: 'Remise (%)',
              key: 'remiseMerged',
              render: (_, r) => {
                // articleRemise is always a number (null treated as 0)
                const articleRemiseValue = Number(r.articleRemise ?? 0);
                const devisRemiseValue = Number(r.REMISE ?? 0);
                const remiseDiffers = r.remiseDiffers || false;
                return (
                  <div>
                    <div>
                      <strong>Devis:</strong> 
                      <span style={remiseDiffers ? { color: '#d4380d', fontWeight: 600 } : {}}>
                        {` ${devisRemiseValue.toFixed(2)}%`}
                      </span>
                    </div>
                    <div>
                      <strong>Article:</strong> 
                      <span style={remiseDiffers ? { color: '#d4380d', fontWeight: 600 } : {}}>
                        {` ${articleRemiseValue.toFixed(2)}%`}
                      </span>
                    </div>
                  </div>
                );
              }
            },
            {
              title: 'Total TTC',
              key: 'totalMerged',
              render: (_, r) => (
                <div>
                  <div><strong>Devis:</strong> {Number(r.devisTotal).toFixed(2)}</div>
                  <div><strong>Article:</strong> {r.articleTotal == null ? 'N/A' : Number(r.articleTotal).toFixed(2)}</div>
                </div>
              )
            },
            { 
              title: 'Diff Total TTC (%)', 
              key: 'diffPct', 
              render: (_, r) => {
                if (r.totalDiffPct == null) return 'N/A';
                const label = `${r.totalDiffPct.toFixed(2)}%`;
                return <Tag color="red">{label}</Tag>;
              }
            },
          ]}
        />
      </Drawer>

      <Drawer
        title="Ajouter un article au devis"
        width={900}
        onClose={() => setIsAddItemDrawerVisible(false)}
        open={isAddItemDrawerVisible}
      >
        <DevisAddItemForm
          refDevis={localDevis.REF_DEV}
          codeEnt={localDevis.CODE_ENT}
          onSuccess={(newItem) => {
            handleAddItemSuccess(newItem);
            setIsAddItemDrawerVisible(false);
          }}
        />
      </Drawer>
          
      <Drawer
        title="Sélectionner un commercial"
        width={720}
        onClose={() => setComDrawerOpen(false)}
        open={comDrawerOpen}
      >
        <ComList onSelectCom={handleComSelect} />
      </Drawer>

      <Drawer
        title="Sélectionner un client"
        width={900}
        onClose={() => setClientDrawerOpen(false)}
        open={clientDrawerOpen}
      >
        <ClientList onSelectClient={handleClientSelect} />
      </Drawer>
      
      <ChangesSummaryModal
        visible={isSummaryModalVisible}
        onClose={() => setIsSummaryModalVisible(false)}
        onConfirm={confirmAndSaveChanges}
        originalBonliv={initialDevis}
        modifiedBonliv={localDevis}
        originalItems={initialItems}
        modifiedItems={localItems}
        loading={isSaving}
      />

      <PrintDialog 
        open={isPrintDialogOpen}
        onClose={() => setIsPrintDialogOpen(false)}
        onPrint={handlePrint}
        onPreview={handlePreview}
        onPrintEmplacement={handlePrintEmplacement}
  onPreviewEmplacement={handlePreviewEmplacement}
      />

      <Modal
        title="Notes du client"
        open={isNotesModalVisible}
        onCancel={() => setIsNotesModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsNotesModalVisible(false)}>
            Fermer
          </Button>
        ]}
        width={600}
      >
        <div style={{ padding: '16px 0', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
          {localDevis?.client?.NOTES || "Aucune note disponible"}
        </div>
      </Modal>
    </motion.div>
  );
};

export default SingleDevis;
